import { type FC } from "react";
import { useTranslation } from "react-i18next";
import {
  Wallet,
  TrendingUp,
  ShoppingBag,
  Gauge,
  Coins,
  Trophy,
  BarChart3,
  type LucideIcon,
} from "lucide-react";
import { DashboardKpiCard } from "src/components/common";
import type {
  PaidMediaOverviewLeadingPlatform,
  PaidMediaOverviewSummaryKpi,
} from "src/services/PaidMediaOverviewAPI";
import { getOverviewTooltip } from "../../utils/paidMediaOverviewTooltips";
import { useOverviewAttribution } from "../../utils/paidMediaOverviewAttributionContext";
import { overviewPlatformLabel } from "../../utils/paidMediaOverviewLabels";
import {
  formatOverviewMultiplier,
  formatOverviewText,
  OVERVIEW_EMPTY_VALUE,
} from "../../utils/paidMediaOverviewFormat";

interface KpiPresentation {
  icon: LucideIcon;
  tooltipId: string;
  trendPolarity: "higher-is-better" | "lower-is-better";
  labelKey?: string;
}

const KPI_PRESENTATION: Record<string, KpiPresentation> = {
  "total-paid-media-spend": {
    icon: Wallet,
    tooltipId: "totalSpend",
    trendPolarity: "higher-is-better",
    labelKey: "paidMediaOverview.kpis.totalSpend",
  },
  "paid-media-revenue": {
    icon: TrendingUp,
    tooltipId: "paidRevenue",
    trendPolarity: "higher-is-better",
    labelKey: "paidMediaOverview.kpis.paidRevenue",
  },
  "paid-purchases": {
    icon: ShoppingBag,
    tooltipId: "paidPurchases",
    trendPolarity: "higher-is-better",
    labelKey: "paidMediaOverview.kpis.paidPurchases",
  },
  "blended-roas": {
    icon: Gauge,
    tooltipId: "blendedRoas",
    trendPolarity: "higher-is-better",
    labelKey: "paidMediaOverview.kpis.blendedRoas",
  },
  "blended-cost-per-purchase": {
    icon: Coins,
    tooltipId: "blendedCpp",
    trendPolarity: "lower-is-better",
    labelKey: "paidMediaOverview.kpis.blendedCpp",
  },
};

const DEFAULT_PRESENTATION: KpiPresentation = {
  icon: BarChart3,
  tooltipId: "totalSpend",
  trendPolarity: "higher-is-better",
};

interface OverviewKpiCardsProps {
  combined: PaidMediaOverviewSummaryKpi[];
  leadingPlatform: PaidMediaOverviewLeadingPlatform | null;
}

const OverviewKpiCards: FC<OverviewKpiCardsProps> = ({ combined, leadingPlatform }) => {
  const { t } = useTranslation();
  const mode = useOverviewAttribution();

  const leadingPlatformName = overviewPlatformLabel(t, leadingPlatform?.platform);
  const leadingPlatformFooter =
    leadingPlatform?.reason?.trim() ||
    t("paidMediaOverview.kpis.leadingPlatformFooter", {
      roas: formatOverviewMultiplier(leadingPlatform?.roas),
    });

  return (
    <div className="paid-media-overview__kpi-grid">
      {combined?.map(kpi => {
        const presentation = KPI_PRESENTATION[kpi?.id] ?? DEFAULT_PRESENTATION;
        const Icon = presentation.icon;
        const title = presentation.labelKey
          ? t(presentation.labelKey)
          : formatOverviewText(kpi?.title);

        return (
          <DashboardKpiCard
            key={kpi?.id ?? title}
            className="paid-media-overview__kpi-card"
            title={title}
            value={formatOverviewText(kpi?.formatted_value)}
            icon={<Icon size={18} />}
            tooltip={getOverviewTooltip(t, presentation.tooltipId, mode)}
            metricLabelForTooltip={title}
            formulaTitle={t("webAnalytics.kpi.formula")}
            changePercent={kpi?.percent_change ?? undefined}
            changePercentFormattedValue={kpi?.formatted_percent_change?.trim() || undefined}
            trendPolarity={presentation.trendPolarity}
            comparisonLabel={t("paidMediaOverview.kpis.comparisonLabel")}
          />
        );
      })}

      <DashboardKpiCard
        className="paid-media-overview__kpi-card"
        title={t("paidMediaOverview.kpis.leadingPlatform")}
        value={leadingPlatformName || OVERVIEW_EMPTY_VALUE}
        icon={<Trophy size={18} />}
        tooltip={getOverviewTooltip(t, "leadingPlatform", mode)}
        metricLabelForTooltip={t("paidMediaOverview.kpis.leadingPlatform")}
        formulaTitle={t("webAnalytics.kpi.formula")}
        footer={leadingPlatformName ? leadingPlatformFooter : undefined}
        hideNoTrendFallback
        comparisonLabel={t("paidMediaOverview.kpis.comparisonLabel")}
      />
    </div>
  );
};

export default OverviewKpiCards;
